/**
 * Die Wertelisten des Vertrags.
 *
 * Sie stehen hier und nicht in der API: Der Vertrag beschreibt, was ein
 * Rapport ist – die API setzt ihn um. Ein Frontend, das gegen diese Listen
 * baut, sieht dieselben Werte wie das OpenAPI-Dokument.
 */

/**
 * Die Gesellschaften der KALLMANN Engineering – dieselben zwei wie im
 * ERP-Hub. Ein Rapport erbt sie vom Auftrag, denn der Auftrag sagt, wer
 * abrechnet; der Techniker entscheidet das nicht.
 */
export const GESELLSCHAFT = ['CH', 'DE'] as const;
export type Gesellschaft = (typeof GESELLSCHAFT)[number];

/**
 * Die Anschrift einer Gesellschaft, wie sie auf das Papier gehört.
 *
 * Sie steht hier und nicht in der Datenbank: Eine Anschrift, die über einen
 * Schreibpfad änderbar wäre, ändert rückwirkend jeden Druck eines
 * verbuchten Rapports – auch den, den der Kunde schon abgelegt hat. Und sie
 * steht nicht in der Umgebung: Wer die Gesellschaften kennt, kennt ihre
 * Adressen; ein fehlender Wert dürfte nicht erst beim Drucken auffallen.
 *
 * Welche gedruckt wird, sagt der Rapport – er erbt die Gesellschaft vom
 * Auftrag (oben). Ein Schweizer Auftrag mit deutschem Briefkopf wäre keine
 * Kleinigkeit: Der Kunde legt das Papier zur Rechnung, und die kommt von
 * der anderen Gesellschaft.
 *
 * **Ein leeres Feld wird nicht gedruckt.** Bankverbindung, Steuernummer und
 * Handelsregister stehen im Fuß, sobald sie hier stehen – bis dahin fehlt im
 * Fuß die Spalte, und es steht keine halbe Angabe auf dem Papier. Eine
 * falsche IBAN auf einem Rapport ist schlimmer als keine.
 *
 * Geschäftsführer, Steuernummer und Register stehen so, wie sie im Impressum
 * stehen – **dieselbe Quelle wie auf der Website**, und dieselben beiden
 * Gesellschaften. Die Bankverbindung steht dort nicht und fehlt darum auch
 * hier; die Spalte kommt, wenn die Angabe kommt.
 */
export type Anschrift = {
  name: string;
  strasse: string;
  ort: string;
  land: string;
  telefon: string;
  mail: string;
  web: string;
  /** Wer zeichnet – wie im Briefbogen: „Geschäftsführer Vorname Name". */
  geschaeftsfuehrer: string;
  bank: string;
  iban: string;
  bic: string;
  /** Mit Bezeichnung, weil die in CH und DE verschieden heißt: `MwSt. DE…`, `UID CHE-…`. */
  steuer: string;
  /** Handelsregister, ebenso mit Bezeichnung: `HRB 732935`, `CH-…`. */
  register: string;
};

/**
 * Beide Gesellschaften heißen `GmbH` – das ist kein Versehen. Auseinander
 * hält sie die Anschrift, nicht der Name; wer den Briefkopf prüft, schaut
 * auf Strasse und Ort.
 */
export const ANSCHRIFT: Record<Gesellschaft, Anschrift> = {
  CH: {
    name: 'KALLMANN Engineering GmbH',
    strasse: 'Spisergasse 21',
    ort: 'CH-9000 St. Gallen',
    land: 'Schweiz',
    telefon: '+41 76 497 20 61',
    mail: 'info@kallmann-engineering.com',
    web: 'www.kallmann-engineering.com',
    geschaeftsfuehrer: 'Geschäftsführer Martin Frischknecht',
    bank: '',
    iban: '',
    bic: '',
    steuer: 'MWST-Nr. CHE-208.806.746 MWST',
    register: 'Handelsregister Kanton St. Gallen, UID CHE-208.806.746',
  },
  DE: {
    name: 'KALLMANN Engineering GmbH',
    strasse: 'Im Kirschgarten 5',
    ort: '79415 Bad Bellingen',
    land: 'Deutschland',
    telefon: '+49 172 7663724',
    mail: 'info@kallmann-engineering.com',
    web: 'www.kallmann-engineering.com',
    geschaeftsfuehrer: 'Geschäftsführer Boris Kallmann',
    bank: '',
    iban: '',
    bic: '',
    steuer: 'USt-IdNr. DE453220497',
    register: 'Amtsgericht Freiburg, HRB 732935',
  },
};

/**
 * Der Lebenszyklus eines Rapports.
 *
 * - `entwurf` – der Techniker schreibt noch. Nur hier ist der Inhalt änderbar.
 * - `abgeschlossen` – der Techniker hat ihn abgeschickt. Der Kunde hat
 *   unterschrieben oder der Einsatz ist zu Ende; inhaltlich ist nichts mehr
 *   zu tun, aber er ist noch nicht auf dem Weg zur Rechnung.
 * - `freigegeben` – die Leitung hat ihn geprüft. **Ab hier steht er in der
 *   Outbox** und wird an das ERP gebucht.
 * - `verbucht` – das ERP hat die Buchung bestätigt. Endzustand.
 * - `storniert` – aus dem Verkehr gezogen. Endzustand, kein Löschen.
 *
 * Zwei Endzustände und kein Löschen: Ein Rapport, der einmal beim Kunden
 * unterschrieben war, verschwindet nicht, er wird storniert – und bleibt
 * nachlesbar. Die erlaubten Übergänge stehen in `uebergang.ts`.
 */
export const RAPPORT_STATUS = [
  'entwurf',
  'abgeschlossen',
  'freigegeben',
  'verbucht',
  'storniert',
] as const;
export type RapportStatus = (typeof RAPPORT_STATUS)[number];

/**
 * Was eine Rapportposition ist – und jede Art wird anders abgerechnet.
 *
 * - `arbeit` – Stunden vor Ort oder in der Werkstatt. Trägt `von` und `bis`.
 * - `reise` – Fahrzeit oder Kilometer. Was davon, sagt die `einheit`.
 * - `material` – verbrauchte Teile, frei benannt.
 * - `spesen` – Übernachtung, Verpflegung, Auslagen.
 *
 * Keine Preise: Der Techniker erfasst Mengen, das ERP kennt die Sätze. Ein
 * Preis im Rapport wäre eine zweite Wahrheit neben der Preisliste.
 */
export const POSITIONSART = ['arbeit', 'reise', 'material', 'spesen'] as const;
export type Positionsart = (typeof POSITIONSART)[number];

/**
 * Was sich erfassen lässt – `spesen` nicht mehr.
 *
 * Die Art bleibt in `POSITIONSART` und damit im Datenbank-Enum stehen: Es
 * gibt Rapporte mit Spesenzeilen, und die bleiben lesbar (kein Löschen).
 * Neue Zeilen entstehen keine mehr – weder über die Auswahl noch über die
 * Knöpfe unter der Liste.
 */
export const POSITIONSART_ERFASSBAR = ['arbeit', 'reise', 'material'] as const;

/**
 * Die Rollen, mit denen ein Mensch hier auftritt – aus Entra abgeleitet
 * (Sicherheitsgruppe oder App-Rolle) und in `mitarbeiter_rolle` gespeichert.
 *
 * - `techniker` – erfasst und schließt eigene Rapporte ab.
 * - `leitung` – sieht alle, gibt frei, storniert.
 * - `admin` – alles, was die Leitung darf, und dazu die Einstellungen:
 *   Tätigkeitsarten, fremde Vorbelegungen, Einrichtung und welche
 *   Entra-Gruppe welche Rolle bedeutet.
 *
 * Fachlich bleiben es zwei: jemand, der den Rapport schreibt, und jemand,
 * der ihn vor der Rechnung prüft. Der Admin richtet ein.
 */
export const ROLLE = ['techniker', 'leitung', 'admin'] as const;
export type Rolle = (typeof ROLLE)[number];

/**
 * Die Grundkonfiguration Gruppe → Rolle, wie sie im Betrieb eingetragen ist
 * (Stand 02.10.2026). Sie wirkt wie `ENTRA_GRUPPE_TECHNIKER` und
 * `ENTRA_GRUPPE_LEITUNG`: **eine Vorlage**, die übernommen wird, wo die
 * Gruppe in `gruppenrolle` noch nie stand – eine neue Datenbank hat sie
 * damit ab dem ersten Zugriff. Danach ist sie eine Zeile wie jede andere,
 * und eine entfernte kommt nicht wieder.
 *
 * Kein Admin darin: Der Anker bleibt `ENTRA_GRUPPE_ADMIN` in der `.env`.
 */
export const GRUPPENROLLEN_VORLAGE: readonly {
  gruppe: string;
  rolle: Exclude<Rolle, 'admin'>;
  bezeichnung: string;
}[] = [
  {
    gruppe: '423fe4fc-8839-4982-89c5-aac69704ffee',
    rolle: 'techniker',
    bezeichnung: 'Mitarbeiter CH',
  },
  {
    gruppe: 'c1928b3d-c771-43fb-9b1b-d007786cc126',
    rolle: 'techniker',
    bezeichnung: 'Mitarbeiter DE',
  },
  {
    gruppe: 'a0b9ba05-159e-4ed3-b031-895dfbfed786',
    rolle: 'leitung',
    bezeichnung: 'Arbeitsrapporte_Leitung',
  },
];

/**
 * Was eine Person mit ihren Rollen tatsächlich ist: **Ein Admin ist auch
 * Leitung.** An einer Stelle, damit keine Prüfung auf `leitung` den Admin
 * vergisst – sie alle fragen weiter nach `leitung`.
 */
export function wirksameRollen(rollen: readonly Rolle[]): Rolle[] {
  const menge = new Set(rollen);

  if (menge.has('admin')) menge.add('leitung');

  return ROLLE.filter((r) => menge.has(r));
}

/** Woher eine Zuordnung Gruppe → Rolle kommt: fest aus der `.env` oder gepflegt in der Oberfläche. */
export const GRUPPENROLLE_QUELLE = ['umgebung', 'oberflaeche'] as const;
export type GruppenrolleQuelle = (typeof GRUPPENROLLE_QUELLE)[number];

/**
 * Woher eine gespeicherte Rolle kommt (`mitarbeiter_rolle.quelle`).
 *
 * - `entra_gruppe` – aus einer Sicherheitsgruppe; abgeglichen bei der
 *   Anmeldung (`groups`) und im stündlichen Gruppenlauf über Graph.
 * - `entra_approlle` – aus einer App-Rolle (`roles`); abgeglichen allein
 *   bei der Anmeldung. Der Gruppenlauf fasst sie nicht an.
 * - `manuell` – von Hand vergeben; kein Abgleich fasst sie an.
 */
export const ROLLEN_QUELLE = ['entra_gruppe', 'entra_approlle', 'manuell'] as const;
export type RollenQuelle = (typeof ROLLEN_QUELLE)[number];

/**
 * Der Stand eines Eintrags in der Outbox.
 *
 * - `offen` – wartet auf den nächsten Versuch.
 * - `gesendet` – der Hub hat die Buchung bestätigt.
 * - `fehlgeschlagen` – nach der letzten Wiederholung aufgegeben. Ein
 *   Mensch sieht nach; die Nutzlast bleibt stehen.
 * - `verworfen` – der Rapport wurde storniert, bevor er gebucht war.
 *   Nichts ging an den Hub, und nichts wird mehr gehen.
 *
 * Ein vierter Wert und kein `fehlgeschlagen` mit Bemerkung: Ein
 * verworfener Eintrag darf nie erneut angestoßen werden, ein
 * fehlgeschlagener schon.
 */
export const VERSAND_STATUS = ['offen', 'gesendet', 'fehlgeschlagen', 'verworfen'] as const;
export type VersandStatus = (typeof VERSAND_STATUS)[number];

/**
 * Die Listen, die dieser Dienst aus dem ERP-Hub spiegelt.
 *
 * Eingangsrechnungen braucht ein Rapport nicht. **Ansprechpartner schon** –
 * seit der Einsatz den Kunden einlädt: Die Mailadresse, an die die
 * Einladung geht, ist im ERP gepflegt und wird nicht abgetippt. Sie stehen
 * im Hub als eigene Liste, weil ein Kontakt mehrere hat.
 *
 * Je Liste ein eigener Cursor, denn jede ist ein eigenes Delta. Die
 * Reihenfolge hier ist die Reihenfolge des Laufs: Ansprechpartner nach den
 * Kontakten, damit ihr `kontaktId` im ersten Lauf auf eine Zeile zeigt, die
 * es schon gibt.
 */
export const HUB_LISTE = ['kontakt', 'auftrag', 'artikel', 'ansprechpartner'] as const;
export type HubListe = (typeof HUB_LISTE)[number];

/**
 * Der Lebenszyklus eines Einsatzes – wer wann zu wem fährt.
 *
 * - `geplant` – die Leitung hat ihn gebucht. Termine sind gestellt, der
 *   Kunde ist eingeladen, seine Antwort steht aus.
 * - `bestaetigt` – der Kunde hat zugesagt. Von Graph gelesen oder von Hand
 *   eingetragen, wenn er auf anderem Weg geantwortet hat.
 * - `abgesagt` – er findet nicht statt. Die Termine werden entfernt.
 * - `erledigt` – er ist gelaufen; ein Rapport hängt dran oder auch nicht.
 *
 * Kein Löschen: Ein Einsatz, zu dem ein Kunde eingeladen wurde, verschwindet
 * nicht – auch nicht, wenn er ausfällt. Sonst stünde beim Kunden ein Termin,
 * den hier niemand mehr kennt.
 */
export const EINSATZ_STATUS = ['geplant', 'bestaetigt', 'abgesagt', 'erledigt'] as const;
export type EinsatzStatus = (typeof EINSATZ_STATUS)[number];

/**
 * Was der eingeladene Ansprechpartner geantwortet hat.
 *
 * Die fünf Werte, die Graph an `attendees[].status.response` führt, plus
 * nichts Eigenes: Wer sie umbenennt, muss die Übersetzung pflegen, und die
 * ginge beim ersten neuen Wert schief.
 *
 * `keine` ist dabei die ehrliche Vorgabe – „noch nicht geantwortet" und
 * „hat abgelehnt" sind verschiedene Dinge, und ein Einsatz ohne Einladung
 * kennt die Frage gar nicht.
 */
export const TERMIN_ANTWORT = [
  'keine',
  'ausstehend',
  'zugesagt',
  'unter_vorbehalt',
  'abgesagt',
] as const;
export type TerminAntwort = (typeof TERMIN_ANTWORT)[number];

/**
 * Woher die Zusage kommt.
 *
 * **Von Hand ist kein Notbehelf, sondern der Regelfall bei manchen Kunden.**
 * Nicht jedes Postfach schickt eine maschinenlesbare Antwort, und mancher
 * schreibt einfach „passt so" zurück. Ohne diesen Weg stünde der Einsatz
 * für immer auf „ausstehend", und niemand wüsste, ob das „nicht gesehen"
 * oder „nicht geantwortet" heißt.
 */
export const ANTWORT_QUELLE = ['kalender', 'hand'] as const;
export type AntwortQuelle = (typeof ANTWORT_QUELLE)[number];

/**
 * Wofür eine Tätigkeitsart steht – Arbeit oder Reise.
 *
 * Nur diese zwei der Positionsarten: Material wird frei benannt (der
 * Techniker schreibt hin, was er verbaut hat), und Spesen werden gar nicht
 * mehr erfasst. Stunden und Kilometer dagegen hängen
 * an einer **Rolle** – dieselbe Stunde kostet als Projektleitung etwas
 * anderes als als Montage.
 */
export const TAETIGKEITSART_ART = ['arbeit', 'reise'] as const;
export type TaetigkeitsartArt = (typeof TAETIGKEITSART_ART)[number];

/** Wie ein Lauf gegen den Hub ausgegangen ist. */
export const LAUF_ERGEBNIS = ['erfolgreich', 'fehlgeschlagen'] as const;
export type LaufErgebnis = (typeof LAUF_ERGEBNIS)[number];
